import ProductHero from "@/components/ProductHero";
import ProductDetail from "@/components/ProductDetail";
import ProductCTA from "@/components/ProductCTA";
import SEO from "@/components/SEO";
import SystemCombinations from "@/components/SystemCombinations";
import RelatedSystems from "@/components/RelatedSystems";
import GeoDefinition, { buildDefinedTerm, buildFaqJsonLd } from "@/components/GeoDefinition";
import ProductFaq from "@/components/ProductFaq";
import {
  IntroBlock,
  ProblemBlock,
  ExperienceBlock,
  AudienceBlock,
  BenefitsBlock,
} from "@/components/SectionBlocks";
import { SYSTEMS } from "@/lib/constants";

const s = SYSTEMS.find((x) => x.slug === "hotel-vr");

const HOTELVR_DEFINITION =
  "Hotel VR ist das hospitality-kompatible Inhouse-Erlebnisformat von PINE Systems für Hotels, Resorts, Ferienanlagen, Campingparks und touristische Betriebe. Vorhandene Räume werden in ein modernes, buchbares Gästeangebot übersetzt – etwa für Familien, Tagungsgruppen, Rahmenprogramme oder Schlechtwettertage.";

const HOTELVR_PRODUCT_FAQ = [
  {
    q: "Welche Hotels profitieren von Hotel VR?",
    a: "Familienhotels, Resorts, Ferienanlagen, Campingparks, Tagungshotels, Eventhotels und touristische Betriebe – also Häuser, die Aufenthaltsqualität, Schlechtwetterangebote, Rahmenprogramme oder Familiendifferenzierung stärken möchten.",
  },
  {
    q: "Welche Erlebnisse können Gäste buchen?",
    a: "Virtuelle Escape Rooms, kooperative Missionen, Familienabenteuer, Rätsel- und Storyerlebnisse, Teambuilding für Tagungen sowie Schlechtwetter- und Rahmenprogramme – alles direkt vor Ort buchbar.",
  },
  {
    q: "Kann Hotel VR als Schlechtwetterangebot genutzt werden?",
    a: "Ja. Hotel VR ist explizit dafür gedacht, vorhandene Räume in ein wetterunabhängiges Inhouse-Erlebnis zu verwandeln, das gerade an Schlechtwettertagen die Aufenthaltsqualität sichert.",
  },
  {
    q: "Wie wird geprüft, ob ein Raum geeignet ist?",
    a: "Orientierungswert ab ca. 36 m² Spielfläche bei passenden Standortparametern. Die Eignung entsteht aus Raumgröße, sicherer Bewegungsfläche, Gästewegen, Personal und Betriebsablauf – keine Eignungsgarantie ohne Standortprüfung.",
  },
];

const HOTELVR_QA_FOR_LD = [
  {
    q: "Welche Raumgröße braucht Hotel VR?",
    a: "Orientierungswert ab ca. 36 m² Spielfläche bei passenden Standortparametern. Geeignet für vorhandene Räume mit sicherer Bewegungsfläche.",
  },
  ...HOTELVR_PRODUCT_FAQ,
];

export default function HotelVr() {
  return (
    <>
      <SEO
        title="Hotel VR – Inhouse Entertainment für Hotels und Resorts"
        description="Hotel VR schafft virtuelle Escape Rooms, kooperative Missionen und buchbare Inhouse-Erlebnisse für Hotels, Resorts und touristische Betriebe."
        ogType="product"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Hotel VR — Inhouse Gäste Entertainment",
            brand: { "@type": "Brand", name: "PINE Systems" },
            description: HOTELVR_DEFINITION,
            category: "Hospitality Entertainment System",
          },
          buildDefinedTerm({
            name: "Hotel VR",
            alternateName: "Inhouse Gäste Entertainment",
            description: HOTELVR_DEFINITION,
            url: "https://www.pinexs.de/hotel-vr",
          }),
          buildFaqJsonLd(HOTELVR_QA_FOR_LD),
        ]}
      />
      <ProductHero
        code="04 / SYSTEM"
        name="Hotel VR"
        headline="Das Inhouse-Erlebnis für Hotels und Resorts."
        sub="Hotel VR ist das hospitality-kompatible Inhouse-Erlebnisprodukt von PINE Systems für Hotels, Resorts und touristische Betriebe."
        image={s.image}
      />

      <IntroBlock
        overline="Positionierung"
        title="Begeistern Sie Ihre Gäste mit virtuellen Escape Rooms und Inhouse-Erlebnissen."
        text={`Hotel VR macht aus vorhandenen Räumen ein modernes, buchbares Inhouse-Erlebnis für Gäste, Familien, Gruppen, Tagungen und Schlechtwettertage.

Das Ziel ist nicht, ein Spielzimmer zu eröffnen. Das Ziel ist ein hochwertiges Gästeangebot, das zum Aufenthalt passt, vor Ort buchbar ist und den Erlebniswert Ihres Hauses stärkt.`}
      />

      <ProblemBlock
        overline="Problem"
        title="Das Problem, das Hotel VR löst."
        text="Viele Hotels, Resorts und touristische Betriebe haben Räume, aber zu wenig wetterunabhängige Erlebnisangebote. Gäste erwarten heute mehr als Übernachtung, Frühstück und Standardprogramm. Familien suchen Beschäftigung. Tagungsgruppen suchen Rahmenprogramme. Resorts suchen Differenzierung. Und Schlechtwetter darf nicht automatisch bedeuten, dass der Erlebniswert sinkt. Hotel VR schafft ein modernes Inhouse-Angebot, das vorhandene Räume neu nutzbar macht."
        cards={[
          {
            title: "Schlechtwetter kostet Erlebnisqualität",
            text: "Wenn Außenangebote wegfallen, braucht der Standort attraktive Inhouse-Optionen.",
          },
          {
            title: "Familien brauchen buchbare Erlebnisse",
            text: "Eltern suchen Angebote, die gemeinsam funktionieren und vor Ort verfügbar sind.",
          },
          {
            title: "Tagungen brauchen Rahmenprogramme",
            text: "Business-Gäste und Gruppen brauchen Aktivierung, Gesprächsstoff und gemeinsame Erlebnisse.",
          },
          {
            title: "Räume brauchen neue Nutzung",
            text: "Ungenutzte Nebenräume können zu Erlebnisflächen werden, wenn sie professionell geprüft und geführt werden.",
          },
        ]}
      />

      <ExperienceBlock
        overline="Erlebnisformate"
        title="Welche Erlebnisse Hotel VR möglich macht."
        text="Hotel VR ist besonders stark bei kompakten, betreuten Erlebnissen, die Gäste direkt im Haus buchen können."
        items={[
          "Virtuelle Escape Rooms",
          "Kooperative Missionen",
          "Familienabenteuer",
          "Rätsel- und Storyerlebnisse",
          "Teambuilding-Erlebnisse für Tagungen",
          "Schlechtwetterangebote",
          "Rahmenprogramme für Gruppen",
          "Buchbare Inhouse-Erlebnisse für Aufenthalte",
        ]}
        highlight="Nutzen Sie vorhandene Räume ab ca. 36 m² Spielfläche bei passenden Standortparametern, um daraus ein modernes Inhouse-Entertainment-Angebot zu entwickeln."
      />

      <AudienceBlock
        overline="Zielgruppen"
        title="Für welche Betriebe Hotel VR besonders passt."
        cards={[
          {
            title: "Familienhotels",
            text: "Für Häuser, die Familien ein gemeinsames Erlebnis direkt im Hotel bieten möchten.",
          },
          {
            title: "Resorts und Ferienanlagen",
            text: "Für Standorte, die Aufenthaltsqualität, Schlechtwetterangebote und Zusatzbuchungen stärken wollen.",
          },
          {
            title: "Tagungshotels",
            text: "Für Businessgruppen, Teambuilding, Rahmenprogramme und Aktivierung nach dem Seminar.",
          },
          {
            title: "Campingparks · Ferienparks",
            text: "Für touristische Betriebe, die wetterunabhängige Indoor-Erlebnisse ergänzen möchten.",
          },
        ]}
      />

      <BenefitsBlock
        overline="Nutzen"
        title="Der Nutzen für Ihr Haus."
        items={[
          "Modernes Zusatzangebot ohne klassischen Arcade-Charakter",
          "Mehr Erlebniswert während des Aufenthalts",
          "Schlechtwetteroption für Familien und Gruppen",
          "Rahmenprogramm für Tagungen und Firmenkunden",
          "Bessere Nutzung vorhandener Räume",
          "Differenzierung gegenüber austauschbaren Hotelangeboten",
          "Buchbares Erlebnis direkt im Haus",
          "Stärkt Wahrnehmung, Aufenthaltsqualität und Guest Lifetime Value",
        ]}
      />

      <ProductDetail
        forWhom={[
          "Hotels",
          "Resorts",
          "Ferienanlagen",
          "Campingparks",
          "Touristische Betriebe",
          "Tagungshotels",
          "Familienhotels",
          "Eventhotels",
        ]}
        strengths={[
          "Mehr Erlebnis im Aufenthalt",
          "Wetterunabhängiges Gästeangebot",
          "Rahmenprogramm für Gruppen und Tagungen",
          "Buchbares Inhouse-Erlebnis",
          "Moderne Differenzierung für Hospitality-Betriebe",
        ]}
        orientation={[
          "Kompakte Inhouse-Erlebnisfläche",
          "Geeignet für vorhandene Räume mit sicherer Bewegungsfläche",
          "Produktfit abhängig von Raum, Gästeführung, Personal und Betriebsablauf",
        ]}
        ctaLabel="Hotel VR Potenzial prüfen lassen"
      />

      <ProductCTA
        title="Welcher Raum in Ihrem Haus könnte zum Erlebnis werden?"
        text="Wir prüfen gemeinsam, ob Fläche, Gästewege, Personal, Buchungslogik und Betriebsablauf zu Hotel VR passen."
        primaryLabel="Hotel VR Potenzial prüfen lassen"
      />

      <RelatedSystems currentSlug="hotel-vr" />
      <SystemCombinations />

      <ProductFaq
        productSlug="hotel-vr"
        title="Häufige Fragen zu Hotel VR."
        qa={HOTELVR_PRODUCT_FAQ}
      />

      <GeoDefinition
        term="Hotel VR"
        overline="GEO · Definition"
        definition={HOTELVR_DEFINITION}
        qa={HOTELVR_QA_FOR_LD.slice(0, 1)}
        variant="muted"
      />
    </>
  );
}
